<script setup lang="ts">
import { computed } from 'vue'
import {
  buildBottleneck,
  buildSummary,
  CTA_SUBTEXT,
  M2_BLOCK,
  M3_BLOCK,
  M5_BLOCK,
  M6_LINE,
  RELIEF_ITEMS,
} from '../../lib/issue_diagnosis/content'
import type { Answers, JudgeResult } from '../../lib/issue_diagnosis/types'

const props = defineProps<{
  result: JudgeResult
  answers: Answers
  contactUrl: string
  reportUrl: string
}>()

const TIMEREX_URL = 'https://timerex.net/s/k.minami_3d78_9da5/7ec91298/'

const hasModule = (id: string) => props.result.modules.includes(id as never)

const summary = computed(() => buildSummary(props.answers))
const bottleneck = computed(() => buildBottleneck(props.result.type, hasModule('M1')))
const ctaSubtext = computed(() => (hasModule('M4') ? CTA_SUBTEXT.m4 : CTA_SUBTEXT.default))

function countLabel(item: JudgeResult['curriculum'][number]): string {
  if (item.prep) return '—'
  if (item.countLabel) return item.countLabel
  return typeof item.count === 'number' ? `${item.count}回` : '—'
}
</script>

<template>
  <div class="issue_diagnosis_proposal">
    <section class="issue_diagnosis_block issue_diagnosis_block_1">
      <p class="issue_diagnosis_result_eyebrow">診断結果</p>
      <h2 class="issue_diagnosis_result_title">貴社に合うのは「{{ result.typeName }}」です</h2>
    </section>

    <section class="issue_diagnosis_block">
      <h3 class="issue_diagnosis_block_heading">いま起きていること</h3>
      <p class="issue_diagnosis_block_text">{{ summary }}</p>
    </section>

    <section class="issue_diagnosis_block">
      <h3 class="issue_diagnosis_block_heading">ボトルネックはここです</h3>

      <div v-if="hasModule('M2')" class="issue_diagnosis_inset">
        <p class="issue_diagnosis_inset_title">{{ M2_BLOCK.title }}</p>
        <p class="issue_diagnosis_inset_body">{{ M2_BLOCK.body }}</p>
      </div>

      <p class="issue_diagnosis_block_text issue_diagnosis_pre_wrap">{{ bottleneck }}</p>
    </section>

    <section class="issue_diagnosis_block">
      <h3 class="issue_diagnosis_block_heading">推奨カリキュラム</h3>
      <div class="issue_diagnosis_table_wrap">
        <table class="issue_diagnosis_curriculum_table">
          <thead>
            <tr>
              <th>順序</th>
              <th>内容</th>
              <th>ねらい</th>
              <th>回数</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in result.curriculum" :key="item.order">
              <td>{{ item.order }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.aim }}</td>
              <td>{{ countLabel(item) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="issue_diagnosis_block">
      <h3 class="issue_diagnosis_block_heading">期間の目安</h3>
      <p class="issue_diagnosis_duration">{{ result.duration }}</p>
    </section>

    <section class="issue_diagnosis_block">
      <h3 class="issue_diagnosis_block_heading">内製化の要否</h3>
      <div class="issue_diagnosis_inhouse">
        <p class="issue_diagnosis_inhouse_text">{{ result.inhouse.text }}</p>
        <p class="issue_diagnosis_inhouse_reason">{{ result.inhouse.reason }}</p>
      </div>

      <div v-if="hasModule('M3')" class="issue_diagnosis_inset">
        <p class="issue_diagnosis_inset_title">{{ M3_BLOCK.title }}</p>
        <div class="issue_diagnosis_table_wrap">
          <table class="issue_diagnosis_comparison_table">
            <thead>
              <tr>
                <th></th>
                <th v-for="col in M3_BLOCK.columns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in M3_BLOCK.rows" :key="row.label">
                <th>{{ row.label }}</th>
                <td v-for="(value, i) in row.values" :key="i">{{ value }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="issue_diagnosis_inset_footer">{{ M3_BLOCK.footer }}</p>
      </div>

      <div v-if="hasModule('M5')" class="issue_diagnosis_inset">
        <p class="issue_diagnosis_inset_title">{{ M5_BLOCK.title }}</p>
        <p class="issue_diagnosis_inset_body">{{ M5_BLOCK.intro }}</p>
        <ul class="issue_diagnosis_inset_list">
          <li v-for="cond in M5_BLOCK.conditions" :key="cond">{{ cond }}</li>
        </ul>
        <p class="issue_diagnosis_inset_footer">{{ M5_BLOCK.footer }}</p>
      </div>
    </section>

    <section class="issue_diagnosis_block">
      <h3 class="issue_diagnosis_block_heading">無料相談でお伝えできること</h3>
      <ul class="issue_diagnosis_relief_list">
        <li v-for="item in RELIEF_ITEMS" :key="item">{{ item }}</li>
      </ul>
    </section>

    <section class="issue_diagnosis_block issue_diagnosis_cta_block">
      <p class="issue_diagnosis_thanks_desc">診断結果レポートをメールでお送りしました。</p>
      <p v-if="hasModule('M6')" class="issue_diagnosis_m6_line">{{ M6_LINE }}</p>
      <p class="issue_diagnosis_cta_subtext">{{ ctaSubtext }}</p>
      <a :href="TIMEREX_URL" class="issue_diagnosis_cta_primary" target="_blank" rel="noopener">日程を選ぶ</a>
      <a :href="contactUrl" class="issue_diagnosis_cta_secondary">無料相談を申し込む</a>
      <p class="issue_diagnosis_thanks_note">
        <a :href="reportUrl" target="_blank" rel="noopener">このレポートのリンクをブラウザで見る</a>
      </p>
    </section>
  </div>
</template>
